import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Crosshair, EyeOff, Search, X } from 'lucide-react';
import kgDatasetService from '../../services/kgDatasetService';
import styles from './KgRecordDrawer.module.scss';
import {
  describeError,
  formatBytes,
  formatDateTime,
  formatNumber,
  getIngestStatusMeta,
} from './kgUtils';

/**
 * One record (screen 6), as a drawer rather than a route.
 *
 * `record_key` is the CUSTOMER's id and real ones contain slashes
 * (`CIR/2026/14`); with a composite key the server also decides how the parts
 * join. A drawer sidesteps URL encoding entirely, and keeps the cohort on screen
 * behind it — so the key is only ever passed back exactly as a list response
 * gave it, never composed here.
 */
const KgRecordDrawer = ({ dataset, recordKey, open, onClose, onFilterByValue, onFocusRecord }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Which text fields are stored but not embedded — read off the mapping config,
  // so "stored, not searchable" can be labelled where someone is reading it.
  const embedByPath = {};
  (dataset.mapping_config?.text || []).forEach((t) => {
    embedByPath[t.path] = Boolean(t.embed);
  });
  const labelByPath = {};
  (dataset.mapping_config?.text || []).forEach((t) => {
    labelByPath[t.path] = t.label;
  });

  const load = useCallback(async () => {
    if (!recordKey) return;
    setLoading(true);
    setError(null);
    try {
      const data = await kgDatasetService.getRecord(dataset.id, recordKey);
      setRecord(data);
    } catch (e) {
      setError(describeError(e, "Couldn't load this record."));
    } finally {
      setLoading(false);
    }
  }, [dataset.id, recordKey]);

  useEffect(() => {
    if (open && recordKey) load();
    if (!open) setRecord(null);
  }, [open, recordKey, load]);

  const ingestMeta = record ? getIngestStatusMeta(record.ingest?.status) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: styles.paper }}
    >
      <div className={styles.head}>
        <div className={styles.headText}>
          <code className={styles.recordKey}>{recordKey}</code>
          {record?.title && <Typography className={styles.title}>{record.title}</Typography>}
        </div>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <X size={18} />
        </IconButton>
      </div>

      <div className={styles.body}>
        {loading && (
          <div className={styles.loading}>
            <CircularProgress size={22} />
          </div>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {record && !loading && (
          <>
            {/* ── Status ── */}
            <div className={styles.metaRow}>
              {ingestMeta && (
                <Tooltip title={ingestMeta.help}>
                  <span className={`${styles.pill} ${styles[ingestMeta.tone]}`}>
                    {ingestMeta.label}
                  </span>
                </Tooltip>
              )}
              {record.date && (
                <span className={styles.metaItem}>
                  {String(record.date).slice(0, 10)}
                  {record.event_ym ? ` · ${record.event_ym}` : ''}
                </span>
              )}
              {record.ingest?.chunk_count > 0 && (
                <span className={styles.metaItem}>
                  {formatNumber(record.ingest.chunk_count)} chunks
                </span>
              )}
              {record.raw_bytes > 0 && (
                <span className={styles.metaItem}>{formatBytes(record.raw_bytes)}</span>
              )}
            </div>

            {record.ingest?.error && (
              <Alert severity="error">
                <strong>Indexing failed.</strong> {record.ingest.error}
              </Alert>
            )}

            {/* The graph only ever shows a bounded neighbourhood, so centring on
                a record is how you explore outward from it. */}
            {onFocusRecord && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Crosshair size={14} />}
                onClick={() => onFocusRecord(recordKey)}
                className={styles.focusButton}
              >
                Show what this connects to
              </Button>
            )}

            {/* ── Facets ── clicking one filters the cohort behind the drawer ── */}
            {record.facets && Object.keys(record.facets).length > 0 && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Facets</h4>
                {Object.entries(record.facets).map(([name, values]) => (
                  <div key={name} className={styles.facetRow}>
                    <span className={styles.facetName}>{name}</span>
                    <div className={styles.facetValues}>
                      {(Array.isArray(values) ? values : [values]).map((v) => (
                        <Chip
                          key={String(v)}
                          size="small"
                          label={String(v)}
                          variant="outlined"
                          onClick={() => onFilterByValue?.(name, v)}
                          className={styles.facetChip}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            )}

            {/* ── Text ── includes embed:false fields, which is the point ── */}
            {record.text && Object.keys(record.text).length > 0 && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Text</h4>
                {Object.entries(record.text).map(([path, value]) => {
                  const embedded = embedByPath[path];
                  return (
                    <div key={path} className={styles.textBlock}>
                      <div className={styles.textHead}>
                        <span className={styles.textLabel}>{labelByPath[path] || path}</span>
                        {/* Being able to read this here is what makes "stored"
                            different from "discarded". */}
                        {embedded ? (
                          <Tooltip title="Vectorised — the agent can find this record by what this text means.">
                            <span className={styles.tagEmbed}>
                              <Search size={10} /> searchable by meaning
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Kept in full and readable, but not vectorised. The agent reaches it by filtering first, then reading the record.">
                            <span className={styles.tagStored}>
                              <EyeOff size={10} /> stored, not searchable
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <pre className={styles.textBody}>{String(value)}</pre>
                    </div>
                  );
                })}
              </section>
            )}

            {/* ── Raw ── */}
            {record.raw && Object.keys(record.raw).length > 0 && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Pushed values</h4>
                <div className={styles.rawGrid}>
                  {Object.entries(record.raw).map(([key, value]) => (
                    <React.Fragment key={key}>
                      <code className={styles.rawKey}>{key}</code>
                      <span className={styles.rawValue}>
                        {value === null || value === undefined ? (
                          <em>empty</em>
                        ) : typeof value === 'object' ? (
                          JSON.stringify(value)
                        ) : (
                          String(value)
                        )}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              </section>
            )}

            <Typography variant="caption" className={styles.footnote}>
              Mapped with version {record.mapping_version} · received{' '}
              {formatDateTime(record.received_at)}. Records keep the facets they were ingested
              with — a later mapping change only applies to records pushed after it.
            </Typography>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default KgRecordDrawer;
